import * as React from 'react';
import strings from '../../../../strings/strings.json';
import { createErrorMessage, fieldErrorCodes, requiredFieldErrorMessage } from 'shared/error-messages';
import TextField from 'components/forms/TextField';
import useModifyRequest from 'hooks/useModifyRequest';
import useNeveForm from 'components/forms/NeveForm';
import TextAreaField from 'components/forms/TextAreaField';
import { BuildCourseContext } from 'contextProviders/BuildCourseContext';
import { ModuleType } from 'models/Module';
import RadioFields, { RadioFieldOption } from 'components/forms/RadioFields';
import { FormLabel } from 'components/forms/FormComponents';
import { FlexColMd, FlexColRg, FlexEnd, FlexMd } from 'components/Elements';
import { ErrorNotifierSlim } from 'components/notifiers/ErrorNotifierSlim';
import { validateThreeCharacterLimit } from 'lib/custom-form-validation';
import { styled } from 'styled-components';
import { sizes } from 'theme';
import Button from 'components/buttons/Button';

type CreateModuleFormProps = {
    closeForm: (moduleId: string) => void;
};

type CreateModuleInput = {
    title: string;
    description: string;
    moduleType?: ModuleType;
    durationInMins: number | null;
};

export default function CreateModuleForm({ closeForm }: CreateModuleFormProps): JSX.Element {
    const course = React.useContext(BuildCourseContext).course!;
    const { refetchCourse, editMode, setCourseState } = React.useContext(BuildCourseContext);
    const [error, setError] = React.useState<string>('');

    const { modifyData: createModule, loading } = useModifyRequest(`modules`, 'POST');

    const {
        register,
        handleSubmit,
        watch,
        formState: { errors },
    } = useNeveForm<CreateModuleInput>({
        title: '',
        description: '',
        durationInMins: null,
        moduleType: undefined,
    });

    const watchedModuleType = watch('moduleType');

    async function onSubmit(formData: CreateModuleInput) {
        if (!formData.moduleType) {
            setError(strings.moduleForm.moduleType.error);
            return;
        }

        if (editMode) {
            setCourseState((prev) => {
                if (!prev) return undefined;
                return {
                    ...prev,
                    modules: [
                        ...prev.modules,
                        {
                            id: '',
                            title: formData.title,
                            description: formData.description,
                            moduleType: formData.moduleType!,
                            dateCreated: '',
                            dateUpdated: '',
                            category: '',
                            order: prev.modules.length + 1,
                            activities: [],
                        },
                    ],
                };
            });
            closeForm('');
        } else {
            const response = await createModule({
                ...formData,
                durationInMins: formData.durationInMins ?? 0,
                courseId: course.id,
            });

            const { errors, value: moduleId } = response;
            if (errors) {
                setError(strings.moduleForm.error);
                return;
            }

            await refetchCourse();
            closeForm(moduleId as string);
        }
    }

    type ModuleTypeOption = {
        moduleType: ModuleType;
        name: string;
        description: string;
        aria: string;
    };

    const MODULE_TYPE_OPTIONS: ModuleTypeOption[] = [
        {
            moduleType: ModuleType.LEARNER_LED,
            name: strings.moduleForm.moduleType.learnerled,
            description: strings.moduleForm.moduleType.learnerledDescription,
            aria: strings.moduleForm.moduleType.learnerledDescription,
        },
        {
            moduleType: ModuleType.PRESENTATION,
            name: strings.moduleForm.moduleType.presentation,
            description: strings.moduleForm.moduleType.presentationDescription,
            aria: strings.moduleForm.moduleType.presentationDescription,
        },
    ];

    const moduleTypeOptions: RadioFieldOption[] = MODULE_TYPE_OPTIONS.map((moduleType) => {
        return {
            title: moduleType.name,
            description: moduleType.description,
            aria: moduleType.aria,
            value: moduleType.moduleType,
            inputProps: register('moduleType'),
        };
    });

    return (
        <ModuleForm onSubmit={handleSubmit(onSubmit)}>
            <FlexColRg>
                <FlexColMd>
                    <FormLabel>{strings.moduleForm.moduleType.label}</FormLabel>
                    <RadioFields options={moduleTypeOptions} />
                </FlexColMd>
                <FlexColMd>
                    <TextField
                        fieldName="moduleTitle"
                        maxLength={100}
                        labelText={strings.moduleForm.titleInputLabel}
                        inputAria={strings.moduleForm.titleInputAria}
                        errorMessage={errors.title?.message}
                        disabled={loading}
                        required
                        {...register('title', {
                            required: {
                                value: true,
                                message: requiredFieldErrorMessage(strings.moduleForm.titleInputLabel),
                            },
                            maxLength: {
                                value: 100,
                                message: createErrorMessage(
                                    strings.moduleForm.titleInputLabel,
                                    fieldErrorCodes.maxLength,
                                ),
                            },
                        })}
                    />
                    <TextAreaField
                        maxLength={400}
                        charactersLeft={400 - watch('description').length}
                        fieldName={strings.moduleForm.descriptionLabel}
                        aria={strings.moduleForm.descriptionAria}
                        errorMessage={errors.description?.message}
                        {...register('description', {
                            maxLength: {
                                value: 400,
                                message: createErrorMessage(
                                    strings.moduleForm.descriptionLabel,
                                    fieldErrorCodes.maxLength,
                                ),
                            },
                        })}
                    />
                    {watchedModuleType === ModuleType.PRESENTATION && (
                        <TextField
                            fieldName="durationInMins"
                            maxLength={3}
                            labelText={strings.moduleForm.duration}
                            inputAria={strings.moduleForm.duration}
                            errorMessage={errors.durationInMins?.message}
                            type="number"
                            width="8rem"
                            unitText={strings.moduleForm.durationUnit}
                            required
                            {...register('durationInMins', {
                                required:
                                    watchedModuleType === ModuleType.PRESENTATION
                                        ? {
                                              value: true,
                                              message: requiredFieldErrorMessage(strings.moduleForm.duration),
                                          }
                                        : false,
                                validate: {
                                    validateThreeCharacterLimit,
                                },
                                maxLength: {
                                    value: 3,
                                    message: createErrorMessage(
                                        strings.moduleForm.duration,
                                        fieldErrorCodes.maxLength,
                                    ),
                                },
                            })}
                        />
                    )}
                </FlexColMd>

                <FlexEnd>
                    <FlexMd>
                        {error && <ErrorNotifierSlim description={error} />}
                        <Button
                            title={strings.moduleForm.submitButton}
                            aria={strings.moduleForm.submitButtonAria}
                            disabled={loading}
                            onClick={handleSubmit(onSubmit)}
                            colour='PRIMARY'
                        />
                    </FlexMd>
                </FlexEnd>
            </FlexColRg>
        </ModuleForm>
    );
}

const ModuleForm = styled.form`
    padding: 0 ${sizes.spacingSm};
`;
